import { z } from 'zod';
import { LetterWordingText } from './awards.js';

/**
 * Sector settings (SL9, PRD §12 and §17 "Sector setup"): a Sector Lead's delegates and the
 * sector's program list. Both live in their own tables (`delegations`, `programs`), so a change
 * here is the change the app runs on; the matching registry keys only carry who may view and edit.
 */

const Ref = z.object({ id: z.string(), name: z.string() });
const Reason = z.string().trim().max(2000);

/** The award types a delegate may enter for their Sector Lead (PRD §12). */
export const DelegateAwardType = z.enum(['SDB', 'PAA', 'TAA']);
export type DelegateAwardType = z.infer<typeof DelegateAwardType>;

/** "Last changed by … on …" for one row, with what the change was in plain words. */
export const SetupChange = z.object({
  by: Ref.nullable(),
  at: z.string(),
  /** "Added", "Renamed", "Retired", "Restored", "Changed", "Turned off", "Turned on". */
  what: z.string(),
});
export type SetupChange = z.infer<typeof SetupChange>;

export const SectorDelegate = z.object({
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    jobTitle: z.string().nullable(),
    sector: Ref.nullable(),
  }),
  awardTypes: z.array(DelegateAwardType),
  /** Their entries go to the Sector Lead first. Off by default (PRD §12). */
  needsApproval: z.boolean(),
  /** Off: they can't enter awards for this sector until it's turned on again. */
  active: z.boolean(),
  lastChanged: SetupChange.nullable(),
});
export type SectorDelegate = z.infer<typeof SectorDelegate>;

export const SectorProgram = z.object({
  id: z.uuid(),
  name: z.string(),
  /** A slug and a number (`ma-cobra-0007`). Made by the app; it never changes. */
  systemId: z.string(),
  status: z.enum(['active', 'retired']),
  /** Awards that name it, at any stage. A used program can be retired, never deleted. */
  awardCount: z.number().int().nonnegative(),
  lastChanged: SetupChange.nullable(),
});
export type SectorProgram = z.infer<typeof SectorProgram>;

export const SectorSetup = z.object({
  sector: Ref,
  /** The sector's Sector Lead today (kept in Admin › Roles & lists). */
  lead: Ref.nullable(),
  canEditDelegates: z.boolean(),
  canEditPrograms: z.boolean(),
  /** `null` for people who may see the programs but not the delegates (delegates, Finance). */
  delegates: z.array(SectorDelegate).nullable(),
  programs: z.array(SectorProgram),
  /** Organization availability for delegated entry; never contains private audience members. */
  recognizeAvailability: z
    .array(
      z.object({
        type: DelegateAwardType,
        state: z.enum(['available', 'award_off', 'audience_off']),
      }),
    )
    .optional(),
});
export type SectorSetup = z.infer<typeof SectorSetup>;

/**
 * The sectors whose setup this person may open, theirs first, each with its Sector Lead today
 * (X5 lists them read-only; a System Admin keeps them in Roles & lists).
 */
export const SectorSetupList = z.object({
  sectors: z.array(Ref.extend({ canEdit: z.boolean(), lead: Ref.nullable() })),
});
export type SectorSetupList = z.infer<typeof SectorSetupList>;

export const SectorParams = z.object({ sectorId: z.string().min(1).max(100) });
export const SectorDelegateParams = SectorParams.extend({ personId: z.uuid() });
export const SectorProgramParams = SectorParams.extend({ programId: z.uuid() });

/** All three types are selected by default; actual entry also needs organization availability. */
export const AddDelegate = z
  .object({
    personId: z.uuid(),
    awardTypes: z.array(DelegateAwardType).min(1).max(3).optional(),
    needsApproval: z.boolean().optional(),
    reason: Reason.optional(),
  })
  .strict();
export type AddDelegate = z.infer<typeof AddDelegate>;

export const UpdateDelegate = z
  .object({
    awardTypes: z.array(DelegateAwardType).min(1).max(3).optional(),
    needsApproval: z.boolean().optional(),
    active: z.boolean().optional(),
    reason: Reason.optional(),
  })
  .strict()
  .refine(
    (v) => v.awardTypes !== undefined || v.needsApproval !== undefined || v.active !== undefined,
    'Change at least one thing',
  );
export type UpdateDelegate = z.infer<typeof UpdateDelegate>;

/**
 * A program's name is the wording in its awards' letters when there's no award name (APP-270), so it
 * refuses what every letter text refuses (SENT-252).
 */
export const ProgramName = LetterWordingText.pipe(z.string().min(1).max(120));

export const AddProgram = z.object({ name: ProgramName, reason: Reason.optional() }).strict();
export type AddProgram = z.infer<typeof AddProgram>;

/** Rename (any time; past awards keep the link), retire (used) or restore. */
export const UpdateProgram = z
  .object({
    name: ProgramName.optional(),
    status: z.enum(['active', 'retired']).optional(),
    reason: Reason.optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.status !== undefined, 'Change at least one thing');
export type UpdateProgram = z.infer<typeof UpdateProgram>;

/** Only a program no award has used can be deleted; it stays in the history (a soft delete). */
export const DeleteProgram = z.object({ reason: Reason.optional() }).strict();
export type DeleteProgram = z.infer<typeof DeleteProgram>;

/** One row's History drawer: every change, newest first, in plain words. */
export const SetupHistory = z.object({
  entries: z.array(
    z.object({
      id: z.uuid(),
      at: z.string(),
      by: z.object({ name: z.string(), roles: z.string().nullable() }),
      onBehalfOf: z.string().nullable(),
      /** Who was signed in when `by` was acted as in Preview as (APP-122, APP-150); else null. */
      previewedBy: z.string().nullable().default(null),
      field: z.string().nullable(),
      before: z.string().nullable(),
      after: z.string(),
      reason: z.string().nullable(),
      source: z.enum([
        'screen',
        'csv_import',
        'directory_sync',
        'system_rule',
        'email',
        'teams',
        'setup_file',
      ]),
    }),
  ),
});
export type SetupHistory = z.infer<typeof SetupHistory>;
